import { useState } from 'react';
import { Input, Label, Message } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Column } from '@tanstack/react-table';
import { FilterActionButtons } from 'components/FilterActionButtons/FilterActionButtons';
import {
  MAX_DE_AMOUNT,
  MAX_UK_AMOUNT,
} from 'components/StandAloneCalculator/filterConfigs.helper';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { TApplication } from 'lendingApi/applications.types';
import { useTrackFilter } from '../useTrackFilter.hook';
import styles from './NumberRangeColumnFilter.module.css';

type TProps = {
  column: Column<TApplication, unknown>;
  onClose: () => void;
};

export const NumberRangeColumnFilter = ({ column, onClose }: TProps) => {
  const { isDE } = useLocale();
  const { trackRangeFilterApplied, trackFilterReset } = useTrackFilter(
    column.columnDef.header,
  );
  const { formatCurrency } = useNumberFormat();
  const filterValue = column.getFilterValue() as [string?, string?];
  const [min, setMin] = useState(filterValue?.[0] ?? '');
  const [max, setMax] = useState(filterValue?.[1] ?? '');
  const [error, setError] = useState(false);
  const maximumAmount = isDE ? MAX_DE_AMOUNT : MAX_UK_AMOUNT;

  const handleApply = () => {
    if (!min && !max) {
      onClose();
      return;
    }

    const minNum = min !== '' ? Number(min) : null;
    const maxNum = max !== '' ? Number(max) : null;

    const numIsOutOfRange =
      (minNum !== null && minNum < 0) ||
      (maxNum !== null && maxNum > Number(maximumAmount));

    const invalidOrder = minNum !== null && maxNum !== null && minNum > maxNum;

    if (invalidOrder || numIsOutOfRange) {
      setError(true);
      return;
    }

    column.setFilterValue([min || '', max || '']);
    trackRangeFilterApplied(min || '', max || '');
    onClose();
  };

  const handleReset = () => {
    setMin('');
    setMax('');
    setError(false);
    column.setFilterValue(undefined);
    trackFilterReset();
  };

  const renderInput = (
    defaultValue: string,
    inputType: string,
    setValue: (value: string) => void,
  ) => {
    return (
      <Input
        placeholder={inputType === 'min' ? '0' : maximumAmount}
        value={defaultValue}
        onChange={(event) => {
          setError(false);
          setValue(event.target.value);
        }}
      />
    );
  };

  return (
    <div className={styles.filter}>
      <div className={styles.inputs}>
        <div className={styles.inputContainer}>
          <Label>
            <Trans>From</Trans>
          </Label>
          {renderInput(min, 'min', setMin)}
        </div>
        <div className={styles.inputContainer}>
          <Label>
            <Trans>To</Trans>
          </Label>
          {renderInput(max, 'max', setMax)}
        </div>
      </div>
      {error && (
        <Message
          className={styles.error}
          message={t`Please enter a range from £0 to ${formatCurrency(maximumAmount)}.`}
          type="error"
          size="small"
        />
      )}
      <FilterActionButtons
        onResetClick={handleReset}
        onApplyClick={handleApply}
      />
    </div>
  );
};
